// Team-space management modal for /memory (opened from the SpaceSwitcher's
// "Create a team space" / "Manage spaces…"). Create a shared space, see members,
// invite someone by email (we email them a one-time link and they join from it; the address is
// where the invite goes, not an account lookup, the same as the iPhone app), copy a link instead,
// see and withdraw waiting invites, remove / leave. Deliberately NO per-node attribution UI:
// captures are LLM distillations of a session, not user statements. V1 shows no
// author labels anywhere — the space is one shared tree.
import { useEffect, useState } from "react";
import {
  Autocomplete, Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle,
  IconButton, Stack, TextField, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import UserAvatar from "./UserAvatar";
import {
  addWorkspaceCoOwner, addWorkspaceMember, createWorkspace, inviteToSpace, listMemoryOrgUsers, listSpaceInvites,
  removeWorkspaceMember, setActiveWorkspace, stepDownWorkspaceCoOwner, withdrawSpaceInvite,
} from "../utils/api";

const until = (ts) => (ts ? new Date(ts * 1000).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");
// An older backend has no invite routes: inviting falls back to adding an existing teammate by name.
const noInvites = (e) => e?.response?.status === 404 && /not found/i.test(e?.response?.data?.error || "");

const TEAL = "#2da894";

function MicroLabel({ children }) {
  return (
    <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 1.2, fontWeight: 700 }}>
      {children}
    </Typography>
  );
}

// One space's card: members + co-owner controls. Co-owners run the space together: each adds and
// removes members, makes others co-owners, and can step down while another co-owner remains. The
// backend decides what the caller may do to each member (`removable`, `can_make_co_owner`); an
// older backend sends neither, and has one owner (`is_owner`), so fall back to that.
// `meUid` distinguishes "Remove" from "Leave".
// `orgUsers` feeds the add-teammate Autocomplete (freeSolo, so a raw email still works).

function SpaceCard({ space, meUid, active, orgUsers, onChanged, onError }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [invites, setInvites] = useState([]);
  const [note, setNote] = useState("");
  const canInvite = space.can_invite ?? space.is_owner;
  const loadInvites = async () => {
    if (!canInvite) return;
    try { setInvites(await listSpaceInvites(space.workspace)); } catch { /* an older backend: none to show */ }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadInvites(); }, [space.workspace, canInvite]);
  const memberEmails = new Set((space.members || []).map((m) => (m.email || "").toLowerCase()));
  const candidates = (orgUsers || []).filter((u) => !memberEmails.has(u.email));

  const run = async (fn) => {
    setBusy(true); onError("");
    try { await fn(); await onChanged(); }
    catch (e) { onError(e?.response?.data?.error || e?.message || "Something went wrong."); }
    finally { setBusy(false); }
  };

  const add = () => run(async () => {
    const to = email.trim();
    setNote("");
    try {
      const r = await inviteToSpace(space.workspace, to);
      setNote(r?.emailed === false ? `Couldn't email ${to}. Copy an invite link and send it to them instead.` : `Invite sent to ${to}.`);
      await loadInvites();
    } catch (e) {
      if (!noInvites(e)) throw e;
      await addWorkspaceMember(space.workspace, to);
    }
    setEmail("");
  });
  const copyLink = () => run(async () => {
    setNote("");
    const r = await inviteToSpace(space.workspace, null);
    try { await navigator.clipboard.writeText(r.url); setNote("Invite link copied. It works once, for 14 days."); }
    catch { setNote(`Invite link: ${r.url}`); }
    await loadInvites();
  });
  const withdraw = (id) => run(async () => { await withdrawSpaceInvite(space.workspace, id); await loadInvites(); });
  const remove = (uid) => run(() => removeWorkspaceMember(space.workspace, uid));
  const makeCoOwner = (uid) => run(() => addWorkspaceCoOwner(space.workspace, uid));
  const stepDown = () => run(() => stepDownWorkspaceCoOwner(space.workspace));
  const coOwner = (m) => m.is_co_owner ?? m.is_owner;
  const leave = () => run(async () => {
    await removeWorkspaceMember(space.workspace, meUid);
    if (active) { await setActiveWorkspace(null); window.location.reload(); }
  });

  return (
    <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{space.name || space.workspace}</Typography>
        <Typography variant="caption" color="text.secondary">· {space.member_count} member{space.member_count !== 1 ? "s" : ""}</Typography>
        {active && (
          <Typography variant="caption" sx={{ color: TEAL, fontWeight: 700 }}>· active</Typography>
        )}
      </Box>

      <Stack spacing={0.75} sx={{ mt: 1.5 }}>
        {(space.members || []).map((m) => (
          <Box key={m.uid} sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <UserAvatar user={{ email: m.email, user_id: m.uid }} size={26} />
            <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
              {m.email || m.uid}
              {m.uid === meUid && <Box component="span" sx={{ color: "text.secondary" }}> (you)</Box>}
            </Typography>
            {m.guest && (
              <Typography variant="caption" title="From outside this space's organization. Their chats run on their own plan."
                          sx={{ color: "text.secondary", fontWeight: 700, bgcolor: "rgba(0,0,0,.05)", px: 1, py: 0.25, borderRadius: 99 }}>Guest</Typography>
            )}
            {coOwner(m) && (
              <Typography variant="caption" sx={{ color: TEAL, fontWeight: 700, bgcolor: "rgba(45,168,148,.1)",
                                                  px: 1, py: 0.25, borderRadius: 99 }}>Co-owner</Typography>
            )}
            {m.can_make_co_owner && (
              <Button size="small" disabled={busy} onClick={() => makeCoOwner(m.uid)}
                      sx={{ textTransform: "none", color: "text.secondary", minWidth: 0 }}>Make co-owner</Button>
            )}
            {(m.removable ?? (space.is_owner && !coOwner(m))) && (
              <Button size="small" disabled={busy} onClick={() => remove(m.uid)}
                      sx={{ textTransform: "none", color: "text.secondary", minWidth: 0 }}>Remove</Button>
            )}
            {/* A co-owner can step down, and can leave outright, while another co-owner remains
                (the backend refuses both for the last one); a member can always leave. */}
            {m.uid === meUid && coOwner(m) && space.can_step_down && (
              <Button size="small" disabled={busy} onClick={stepDown}
                      sx={{ textTransform: "none", color: "text.secondary", minWidth: 0 }}>Step down</Button>
            )}
            {m.uid === meUid && (!coOwner(m) || space.can_step_down) && (
              <Button size="small" disabled={busy} onClick={leave}
                      sx={{ textTransform: "none", color: "text.secondary", minWidth: 0 }}>Leave</Button>
            )}
          </Box>
        ))}
      </Stack>

      {invites.length > 0 && (
        <Stack spacing={0.5} sx={{ mt: 1 }} data-testid="waiting-invites">
          {invites.map((i) => (
            <Box key={i.invite_id} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" color="text.secondary" noWrap sx={{ flex: 1, minWidth: 0 }}>
                {i.email ? `Invited: ${i.email}` : "Invite link, not used yet"}{i.expires_at ? ` · until ${until(i.expires_at)}` : ""}
              </Typography>
              <Button size="small" disabled={busy} onClick={() => withdraw(i.invite_id)}
                      sx={{ textTransform: "none", color: "text.secondary", minWidth: 0 }}>Withdraw</Button>
            </Box>
          ))}
        </Stack>
      )}

      {canInvite && (
        <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
          <Autocomplete
            size="small" fullWidth freeSolo
            options={candidates}
            getOptionLabel={(u) => (typeof u === "string" ? u : u.email)}
            renderOption={(props, u) => (
              <Box component="li" {...props} key={u.email}
                   sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <UserAvatar user={{ email: u.email, display_name: u.display_name }} size={22} tooltip={false} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" noWrap>{u.display_name || u.email}</Typography>
                  {u.display_name && (
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                      {u.email}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            inputValue={email}
            onInputChange={(_, v) => setEmail(v)}
            onChange={(_, v) => setEmail(typeof v === "string" ? v : (v?.email || ""))}
            renderInput={(params) => (
              <TextField {...params} placeholder="Invite by email…"
                         onKeyDown={(e) => { if (e.key === "Enter" && email.trim()) add(); }} />
            )}
          />
          <Button variant="contained" size="small" disabled={busy || !email.trim()} onClick={add}
                  sx={{ textTransform: "none", borderRadius: 1.5, boxShadow: "none", bgcolor: TEAL,
                        "&:hover": { bgcolor: "#1f8a78" } }}>
            Invite
          </Button>
        </Box>
      )}
      {canInvite && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.75, flexWrap: "wrap" }}>
          <Button size="small" disabled={busy} onClick={copyLink} sx={{ textTransform: "none", color: TEAL, px: 0, minWidth: 0 }}>
            Or copy an invite link
          </Button>
          <Typography variant="caption" color="text.secondary">They'll see everything in this space.</Typography>
        </Box>
      )}
      {note && <Typography variant="body2" sx={{ color: TEAL, mt: 1 }}>{note}</Typography>}
    </Box>
  );
}

// This panel is about people. The team plan (one per org) lives on /agent/account as TeamPlanCard.
// `only` = a workspace id: the panel shows THAT space's members and nothing else — no create form,
// no list of every space (opening "Members" on one space and seeing all of them was the confusion).
// `onShowAll` is the escape hatch to the full manager. Without `only`, it is the full manager.
export default function TeamSpacesPanel({ open, onClose, workspaces, meUid, onRefresh, only = null, onShowAll = null }) {
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [orgUsers, setOrgUsers] = useState([]);
  const allSpaces = workspaces?.spaces || [];
  const spaces = only ? allSpaces.filter((s) => s.workspace === only) : allSpaces;
  const active = workspaces?.active || null;
  const onlyName = only ? (spaces[0]?.name || spaces[0]?.workspace || only) : null;

  // Teammate directory for the add Autocomplete — fetched when the panel opens, once.
  useEffect(() => {
    if (!open || orgUsers.length) return;
    let alive = true;
    listMemoryOrgUsers()
      .then((d) => { if (alive) setOrgUsers(d?.users || []); })
      .catch(() => {});                       // freeSolo still lets a raw email through
    return () => { alive = false; };
  }, [open, orgUsers.length]);

  const create = async () => {
    setCreating(true); setError("");
    try {
      await createWorkspace(name.trim());
      setName("");
      await onRefresh();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Could not create the space.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <GroupsOutlinedIcon sx={{ color: TEAL }} fontSize="small" />
        {only ? <>Members · {onlyName}</> : "Team spaces"}
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2.5}>
          {!only && <Box>
            <MicroLabel>CREATE A SPACE</MicroLabel>
            <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
              <TextField
                size="small" fullWidth placeholder="Space name (e.g. billing-team)…"
                value={name} onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && name.trim()) create(); }}
              />
              <Button variant="contained" size="small" disabled={creating || !name.trim()} onClick={create}
                      sx={{ textTransform: "none", borderRadius: 1.5, boxShadow: "none", bgcolor: TEAL,
                            "&:hover": { bgcolor: "#1f8a78" } }}>
                {creating ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Create"}
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              A shared memory tree your teammates can read and contribute to. Your personal memory stays private.
            </Typography>
          </Box>}

          {spaces.length > 0 && (
            <Box>
              {!only && <MicroLabel>YOUR SPACES ({spaces.length})</MicroLabel>}
              <Stack spacing={1.5} sx={{ mt: 0.5 }}>
                {spaces.map((s) => (
                  <SpaceCard key={s.workspace} space={s} meUid={meUid} orgUsers={orgUsers}
                             active={active === s.workspace} onChanged={onRefresh} onError={setError} />
                ))}
              </Stack>
            </Box>
          )}

          {only && onShowAll && (
            <Button size="small" onClick={onShowAll} sx={{ alignSelf: "flex-start", textTransform: "none", color: TEAL, fontWeight: 600 }}>
              Manage all spaces…
            </Button>
          )}

          {!only && <Box sx={{ bgcolor: "action.hover", borderRadius: 2, p: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>Pin one project to a space</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
              You can also switch one running session by telling your assistant{" "}
              <em>"switch my memory to &lt;space&gt;"</em> (plugin v0.2.4+). To route a single repo
              regardless of the default, set{" "}
              <Box component="code" sx={{ fontFamily: "monospace", fontSize: 11.5, color: TEAL }}>
                ASSERTION_WORKSPACE=&lt;space&gt;
              </Box>{" "}
              in that project's environment.
            </Typography>
          </Box>}

          {error && <Typography variant="caption" color="error">{error}</Typography>}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
